import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import type { OperationResult, Portfolio, PortfolioSummary } from 'venn-api';
import { getDraftSummary, getFund, getSpecificPortfolioV3, getSummary } from 'venn-api';
import type { BenchmarkConstraint, ObjectiveType } from 'venn-utils';
import {
  AnalysisSubject,
  getAppliedBenchmarkKey,
  isBenchmarkConstraintActive,
  logExceptionIntoSentry,
  useApi,
  useHasFF,
  useIsMounted,
} from 'venn-utils';
import type { PortfolioLabBenchmarkState } from 'venn-components';
import { proxySetIdState } from 'venn-state';
import { isNil } from 'lodash';
import type { PortfolioLabStoredSettings } from './useValuesFromHistoryState';

interface UseBenchmarkValue extends PortfolioLabBenchmarkState {
  benchmarkSummary: PortfolioSummary | undefined;
  benchmarkSummaryError: boolean;
}

const useBenchmark = (
  portfolio: Portfolio | undefined,
  settings: PortfolioLabStoredSettings,
  storeSettings: (values: Partial<PortfolioLabStoredSettings>) => void,
  benchmarkConstraint: BenchmarkConstraint | undefined,
  objective: ObjectiveType | undefined,
): UseBenchmarkValue => {
  const hasPortfolioLabFF = useHasFF('portfolio_lab_ff');
  const proxySetId = useRecoilValue(proxySetIdState);
  const [benchmarkSubject, setBenchmarkSubjectInState] = useState<AnalysisSubject | undefined>();
  const benchmarkPseudoPortfolio: Portfolio | undefined = useMemo(() => {
    if (benchmarkSubject?.type !== 'investment') {
      return benchmarkSubject?.portfolio;
    }
    const now = Date.now();
    return {
      id: now,
      allocation: portfolio?.allocation,
      name: benchmarkSubject.name,
      draft: true,
      demo: false,
      master: false,
      closingAllocationsTs: [],
      historical: false,
      absNetAssets: 0,
      designation: 'DEFAULT',
      lots: [],
      taxStatuses: [],
      children: [
        {
          id: now + 1,
          allocation: portfolio?.allocation,
          name: benchmarkSubject.name,
          fund: benchmarkSubject.fund,
          draft: true,
          demo: false,
          master: false,
          children: [],
          closingAllocationsTs: [],
          historical: false,
          absNetAssets: 0,
          designation: 'DEFAULT',
          lots: [],
          taxStatuses: [],
        },
      ],
    };
  }, [benchmarkSubject, portfolio]);

  const setBenchmarkSubject = useCallback(
    (benchmark: AnalysisSubject | undefined) => {
      setBenchmarkSubjectInState(benchmark);
      storeSettings({ benchmarkSubject: benchmark });
    },
    [storeSettings],
  );

  const getPortfolioApiRef = useRef(useApi(getSpecificPortfolioV3));
  const getFundApiRef = useRef(useApi(getFund));
  const isMountedRef = useIsMounted();

  // Whether the user has explicitly picked a benchmark, as opposed to it being auto-derived below.
  // A manual pick wins over the constraint/primary-compare defaults, but only until the constraint
  // itself changes (a new benchmark, or being removed) or a different portfolio is selected - at
  // that point the constraint (or lack thereof) takes back over, per product decision: a constraint
  // change always has the final say, a manual pick only sticks between constraint changes.
  const lastPortfolioIdRef = useRef<number | undefined>();
  const lastConstraintKeyRef = useRef<string | undefined>();
  const hasManualBenchmarkRef = useRef(false);
  const hasAttemptedRestoreRef = useRef(false);

  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }

    if (lastPortfolioIdRef.current !== portfolio?.id) {
      lastPortfolioIdRef.current = portfolio?.id;
      hasManualBenchmarkRef.current = false;
    }

    const constraintKey = hasPortfolioLabFF ? getAppliedBenchmarkKey(benchmarkConstraint, objective) : undefined;
    if (lastConstraintKeyRef.current !== constraintKey) {
      lastConstraintKeyRef.current = constraintKey;
      hasManualBenchmarkRef.current = false;
    }

    if (isNil(portfolio) || hasManualBenchmarkRef.current) {
      return;
    }

    // On (re)entry to Portfolio Lab, rehydrate the benchmark the user previously had for this portfolio
    // (persisted in settings when they navigated away), rather than re-deriving from the primary compare.
    // Skipped when a constraint is active - a constraint dictates the benchmark on re-entry per product.
    // Uses the raw state setter (not setBenchmarkSubject) so it never re-persists to history/localStorage:
    // the value is already there, this only rehydrates local state. Runs once so a later user edit or
    // auto-derive is free to take over.
    if (!hasAttemptedRestoreRef.current) {
      hasAttemptedRestoreRef.current = true;
      const constraintActive = hasPortfolioLabFF && isBenchmarkConstraintActive(benchmarkConstraint, objective);
      if (!constraintActive && settings.portfolioId === portfolio.id && !isNil(settings.benchmarkSubject)) {
        setBenchmarkSubjectInState(settings.benchmarkSubject);
        hasManualBenchmarkRef.current = true;
        return;
      }
    }

    const fetchBenchmark = async (portfolioId: number | undefined, fundId: string | undefined) => {
      try {
        if (portfolioId) {
          const { content } = await getPortfolioApiRef.current(portfolioId, undefined, proxySetId);
          setBenchmarkSubject(new AnalysisSubject(content, 'portfolio', proxySetId));
        } else if (fundId) {
          const { content } = await getFundApiRef.current(fundId, proxySetId);
          setBenchmarkSubject(new AnalysisSubject(content, 'investment', proxySetId));
        } else {
          setBenchmarkSubject(undefined);
        }
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        if (e.name !== 'AbortError') {
          setBenchmarkSubject(undefined);
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          logExceptionIntoSentry(e);
        }
      }
    };

    // Is `benchmarkSubject` already resolved to this portfolioId/fundId? Guards the fetches below
    // against looping: they call setBenchmarkSubject, which changes `benchmarkSubject` (a dependency
    // of this effect), so without this check every render would re-trigger another fetch.
    const isResolvedTo = (targetPortfolioId: number | undefined, targetFundId: string | undefined) => {
      if (!isNil(targetPortfolioId)) {
        return benchmarkSubject?.type === 'portfolio' && benchmarkSubject.id === targetPortfolioId;
      }
      if (!isNil(targetFundId)) {
        return benchmarkSubject?.type === 'investment' && benchmarkSubject.id === targetFundId;
      }
      return isNil(benchmarkSubject);
    };

    // A relative-to-benchmark constraint takes priority over the portfolio's own primary compare
    // benchmark: the optimization is already running against it (see currentPolicyWithConstraints
    // in useConstraints.ts, which applies the constraint as soon as it's set, independent of
    // isInPortfolioPolicy), so results should default to comparing against the same benchmark.
    // Except while maximizing Sharpe, where the constraint has no effect on the optimization at all.
    if (hasPortfolioLabFF && isBenchmarkConstraintActive(benchmarkConstraint, objective)) {
      if (!isResolvedTo(benchmarkConstraint?.benchmarkStrategyId, benchmarkConstraint?.benchmarkFundId)) {
        fetchBenchmark(benchmarkConstraint?.benchmarkStrategyId, benchmarkConstraint?.benchmarkFundId);
      }
      return;
    }

    const primaryBenchmark = (portfolio.compare ?? []).find((compare) => compare.primary);
    if (!isNil(primaryBenchmark)) {
      if (!isResolvedTo(primaryBenchmark.portfolioId, primaryBenchmark.fundId)) {
        fetchBenchmark(primaryBenchmark.portfolioId, primaryBenchmark.fundId);
      }
      return;
    }

    // Neither applies (e.g. right after removing a constraint with no primary compare either) - no benchmark.
    if (!isResolvedTo(undefined, undefined)) {
      setBenchmarkSubject(undefined);
    }
  }, [
    proxySetId,
    portfolio,
    hasPortfolioLabFF,
    benchmarkConstraint,
    objective,
    benchmarkSubject,
    settings.portfolioId,
    settings.benchmarkSubject,
    isMountedRef,
    setBenchmarkSubject,
  ]);

  const [summary, setSummary] = useState<PortfolioSummary | undefined>();
  const [summaryError, setSummaryError] = useState(false);
  const getSummaryApiRef = useRef(
    useApi((portfolio: Portfolio, getById: boolean, signal?: AbortSignal) =>
      getBenchmarkSummary(portfolio, getById, proxySetId ?? null, signal),
    ),
  );
  useEffect(() => {
    const fetchSummary = async (portfolioDraft: Portfolio, getById: boolean) => {
      try {
        const { content } = await getSummaryApiRef.current(portfolioDraft, getById);
        setSummary(content);
        setSummaryError(false);
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        if (e.name !== 'AbortError') {
          setSummary(undefined);
          setSummaryError(true);
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          logExceptionIntoSentry(e);
        }
      }
    };
    if (!isNil(benchmarkPseudoPortfolio)) {
      fetchSummary(benchmarkPseudoPortfolio, benchmarkSubject?.type === 'portfolio');
    } else {
      setSummary(undefined);
      setSummaryError(false);
    }
  }, [benchmarkPseudoPortfolio, benchmarkSubject]);

  const onChangeBenchmark = useCallback(
    (updatedBenchmark: AnalysisSubject | undefined) => {
      hasManualBenchmarkRef.current = true;
      setBenchmarkSubject(updatedBenchmark);
      if (isNil(updatedBenchmark)) {
        setSummary(undefined);
      }
    },
    [setBenchmarkSubject],
  );

  return {
    benchmarkPseudoPortfolio,
    benchmarkSubject,
    benchmarkSummary: summary,
    benchmarkSummaryError: summaryError,
    onChangeBenchmark,
  };
};

export default useBenchmark;

const getBenchmarkSummary = async (
  body: Portfolio,
  getById: boolean,
  proxySetId: string | null,
  signal?: AbortSignal,
): Promise<OperationResult<PortfolioSummary>> => {
  if (getById) {
    return getSummary(body.id, proxySetId ?? undefined, signal);
  }
  return getDraftSummary(body, signal);
};
